import { FormatService } from '@/app/common/format/format.service';
import { SubmittedPostVersionEnum } from '@/app/common/general-types/briefs.dto';
import { TtoCampaignVideoDto } from '@/app/common/general-types/tiktok-one-reporting.dto';
import { unwrapLatestResults } from '@/app/atoms/individual-post-results/individual-post-results.helpers';
import { ManageBriefTabsService } from '@/app/pages/manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import { SuperSocialService } from '@/app/services/super-social/super-social.service';
import { UltraSocialService } from '@/app/services/ultra-social/ultra-social';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { AnalysisOverview } from './tabs/analysis-overview/analysis-overview';
import { AnalysisEngagement } from './tabs/analysis-engagement/analysis-engagement';
import { AnalysisViewers } from './tabs/analysis-viewers/analysis-viewers';
import { MatSliderModule } from '@angular/material/slider';
import { DecimalPipe } from '@angular/common';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { AnalysisBoostingComponent } from './tabs/analysis-boosting/analysis-boosting';
import { AnalysisBoostingV2Component } from './tabs/analysis-boosting-v2/analysis-boosting-v2';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

@Component({
  selector: 'ttor-video-analysis',
  imports: [AnalysisOverview, AnalysisEngagement, AnalysisViewers,
    MatSliderModule, DecimalPipe, NgxEchartsDirective,
    AnalysisBoostingComponent, AnalysisBoostingV2Component],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './video-analysis.html',
  styleUrl: './video-analysis.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TtoVideoAnalysisComponent implements OnInit, OnDestroy {

  readonly video = input.required<TtoCampaignVideoDto>();

  readonly close = output<void>();


  selectedTab = signal<'overview' | 'viewers' | 'engagement' | 'boosting' | 'boosting-v2'>('overview');

  /** Playable file URL. Super Social supplies an S3 mp4; Ultra Social supplies the submitted video. */
  playbackUrl = signal<string | undefined>(undefined);

  /** True after user starts playback; thumbnail overlay fades out. */
  playbackStarted = signal(false);

  /** Current video time in whole seconds, drives slider position. */
  currentTime = signal(0);

  private videoRef = viewChild<ElementRef<HTMLVideoElement>>('analysisVideo');

  showPlayButton = computed(() => !!this.playbackUrl() && !this.playbackStarted());

  /** Retention value (0–100) at the current slider position. */
  retentionAtCurrentTime = computed(() => {
    const idx = this.currentTime();
    const retention = this.video()?.video_view_retention;
    if (!retention || idx < 0 || idx >= retention.length) return null;
    return retention[idx]*100;
  });

  /** Base ECharts option — series data, axes, grid, tooltip. Rebuilds only when the video changes. */
  retentionChartOption = computed<EChartsOption>(() => {
    const data = this.video()?.video_view_retention ?? [];
    const primaryColor = this.getCssVar('--primary', '#6508fb');
    const cardColor = this.getCssVar('--card', '#ffffff');
    const borderColor = this.getCssVar('--border', '#e0e0f0');
    const textColor = this.getCssVar('--foreground', '#2a2a4a');
    return {
      animation: false,
      backgroundColor: 'transparent',
      grid: { left: 10, right: 10, top: 16, bottom: 8, containLabel: false },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'none' },
        backgroundColor: cardColor,
        borderColor: borderColor,
        borderWidth: 1,
        textStyle: { color: textColor, fontSize: 12, fontWeight: 'bold' },
        formatter: (params: unknown) => {
          const first = (Array.isArray(params) ? params[0] : params) as { dataIndex: number };
          const idx = first.dataIndex;
          const pct = Math.round((data[idx] ?? 0) * 100);
          return `${this.formatTime(idx)}<br/>${pct}%`;
        },
      },
      xAxis: {
        type: 'category',
        data: data.map((_, i) => i),
        show: false,
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 1,
        show: false,
        splitLine: {
          show: true,
          lineStyle: { type: 'dashed', color: 'rgba(255,255,255,0.15)', width: 1 },
        },
        splitNumber: 2,
      },
      series: [{
        type: 'line',
        data: data,
        smooth: true,
        symbol: 'none',
        lineStyle: { color: primaryColor, width: 2.5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: this.hexToRgba(primaryColor, 0.35) },
              { offset: 1, color: this.hexToRgba(primaryColor, 0.02) },
            ],
          },
        },
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          lineStyle: { color: primaryColor, width: 1.5 },
          label: { show: false },
          data: [{ xAxis: 0 }],
        },
        markPoint: {
          silent: true,
          symbol: 'circle',
          symbolSize: 10,
          itemStyle: { color: cardColor, borderColor: primaryColor, borderWidth: 2.5 },
          label: { show: false },
          data: [{ coord: [0, data[0] ?? 0] } as never],
        },
      }],
    };
  });

  /** Merged patch — updates only the markLine and markPoint position as currentTime changes. */
  retentionChartMerge = computed<EChartsOption>(() => {
    const idx = this.currentTime();
    const data = this.video()?.video_view_retention ?? [];
    const y = data[idx] ?? 0;
    return {
      series: [{
        markLine: { data: [{ xAxis: idx }] },
        markPoint: { data: [{ coord: [idx, y] } as never] },
      }],
    };
  });

  /** services */
  public formatService = inject(FormatService);
  private superSocialService = inject(SuperSocialService);
  private ultraSocialService = inject(UltraSocialService);
  private manageBriefTabsService = inject(ManageBriefTabsService);
  /** services END */

  private readonly subscriptionList = new Subscription();

  ngOnInit(): void {
    this.findOriginalVideo();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  findOriginalVideo(): void {
    const videoId = this.video().video_info.video_id;
    if (!videoId) {
      return;
    }

    const brief = this.manageBriefTabsService.focussedBrief();
    // A piece brief has no shout brief; its posts are always collected through Ultra Social.
    const ultra = !!brief?.pieces?.length || brief?.shoutBrief?.resultsVersion === SubmittedPostVersionEnum.UltraSocial;
    const request$ = ultra
      ? this.ultraSocialService.getCompleteLatestResultsForPosts([videoId])
      : this.superSocialService.getLatestResultsForPosts([videoId]);

    this.subscriptionList.add(
      request$.pipe(take(1)).subscribe({
        next: (payload) => {
          const url = ultra
            ? playbackUrlFromUltra(payload, videoId)
            : playbackUrlFromSuper(payload, videoId);
          this.playbackUrl.set(url);
        },
        error: () => {
          this.playbackUrl.set(undefined);
        },
      }),
    );
  }


  setSelectedTab(tab: 'overview' | 'viewers' | 'engagement' | 'boosting' | 'boosting-v2') {
    this.selectedTab.set(tab);
  }

  getSelectedTabClass(tab: 'overview' | 'viewers' | 'engagement' | 'boosting' | 'boosting-v2') {
    if (this.selectedTab() == tab) {
      return 'border-[var(--primary)] text-[var(--primary)]';
    }
    return 'border-transparent text-muted-foreground hover:border-border hover:text-foreground';
  }

  getSelectedTabIconClass(tab: 'overview' | 'viewers' | 'engagement' | 'boosting' | 'boosting-v2') {
    if (this.selectedTab() == tab) {
      return 'text-[var(--primary)]';
    }
    return 'text-muted-foreground group-hover:text-foreground';
  }

  private getCssVar(name: string, fallback = ''): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(101,8,251,${alpha})`;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  closeVideoFeedback() {
    this.close.emit();
  }

  onPlayClick(videoEl: HTMLVideoElement): void {
    this.playbackStarted.set(true);
    queueMicrotask(() => {
      void videoEl.play();
    });
  }

  onVideoClick(videoEl: HTMLVideoElement): void {
    if (!this.playbackStarted()) {
      this.onPlayClick(videoEl);
      return;
    }
    if (videoEl.paused) {
      void videoEl.play();
    } else {
      videoEl.pause();
    }
  }

  onTimeUpdate(videoEl: HTMLVideoElement): void {
    this.currentTime.set(Math.floor(videoEl.currentTime));
  }

  onSliderChange(value: number): void {
    this.currentTime.set(value);
    const el = this.videoRef()?.nativeElement;
    if (el) {
      el.currentTime = value;
    }
  }

  formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' + s : s}`;
  }

  getVideoLengthTime() {
    const seconds = this.video().video_view_retention.length;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds}`;
  }
}

interface UltraSubmittedVideo {
  fullUrl?: string | null;
  hlsUrl?: string | null;
}

interface UltraSubmittedPost {
  ss_post_id?: string | null;
  fullUrl?: string | null;
  videoLink?: string | null;
  video?: UltraSubmittedVideo | null;
}

function playbackUrlFromSuper(payload: unknown, videoId: string): string | undefined {
  const file = unwrapLatestResults(payload)[videoId]?.['tiktokVideoFile'];
  if (!file || typeof file !== 'object') {
    return undefined;
  }
  const s3Url = (file as { s3_url?: unknown }).s3_url;
  return typeof s3Url === 'string' && s3Url.trim().length > 0 ? s3Url : undefined;
}

function playbackUrlFromUltra(payload: unknown, videoId: string): string | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const posts = (payload as { submittedPosts?: unknown }).submittedPosts;
  if (!Array.isArray(posts)) {
    return undefined;
  }
  const match = posts.find(
    (post): post is UltraSubmittedPost =>
      !!post && typeof post === 'object' && postMatchesVideoId(post as UltraSubmittedPost, videoId),
  );
  const fullUrl = match?.video?.fullUrl?.trim();
  if (fullUrl) {
    return fullUrl;
  }
  const hlsUrl = match?.video?.hlsUrl?.trim();
  return hlsUrl || undefined;
}

function postMatchesVideoId(post: UltraSubmittedPost, videoId: string): boolean {
  return [post.ss_post_id, post.fullUrl, post.videoLink].some((candidate) => {
    if (!candidate) {
      return false;
    }
    return candidate === videoId || tiktokVideoIdFromValue(candidate) === videoId;
  });
}

function tiktokVideoIdFromValue(value: string): string | null {
  const trimmed = value.trim();
  const urlMatch = trimmed.match(/tiktok\.com\/[^?\s]*\/(?:video|photo)\/(\d+)/i);
  if (urlMatch?.[1]) {
    return urlMatch[1];
  }
  return /^\d{10,20}$/.test(trimmed) ? trimmed : null;
}
